/**
 * ユーザーの編集(Drawer)。プロファイル(メール・姓・名・カスタム属性)と
 * アクセス設定(状態・直接ロール)を 1 つのフォームで保存する。
 * 読み取り専用の値は詳細ページのタブに出すので、ここには編集できる欄だけ。
 */
import { useMutation } from "@tanstack/react-query";
import { Button, Drawer, Flex, Form, Input, Select, Space, Switch, Tag, message } from "antd";
import { useEffect } from "react";

import type { IdpUserDetail } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { errorText, useInvalidateIdp, useRoleOptions } from "./hooks";
import { AttrFields, type UserValues } from "./userDialogs";

export function UserEditDrawer({
  user,
  open,
  onClose,
}: {
  user: IdpUserDetail;
  open: boolean;
  onClose: () => void;
}) {
  const api = useApi();
  const roleOpts = useRoleOptions();
  const invalidate = useInvalidateIdp();
  const [form] = Form.useForm<UserValues>();

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      email: user.profile.email,
      firstName: user.profile.firstName,
      lastName: user.profile.lastName,
      enabled: user.access.enabled,
      roles: user.access.directRoles,
      attrs: { ...user.profile.attributes },
    });
  }, [open, user, form]);

  const save = useMutation({
    mutationFn: (v: UserValues) =>
      api.updateIdpUser(user.id, {
        email: v.email ?? "",
        firstName: v.firstName ?? "",
        lastName: v.lastName ?? "",
        enabled: v.enabled ?? true,
        roles: v.roles ?? [],
        attributes: v.attrs ?? {},
      }),
    onSuccess: () => {
      invalidate();
      void message.success("保存した");
      onClose();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  return (
    <Drawer
      open={open}
      width={560}
      onClose={onClose}
      destroyOnClose
      title={`${user.displayName || user.username} を編集`}
      extra={
        <Button type="primary" loading={save.isPending} onClick={() => void form.submit()}>
          保存
        </Button>
      }
    >
      <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
        <Form.Item label="ユーザー名(変えられない)">
          <Input value={user.profile.username} disabled />
        </Form.Item>
        <Form.Item name="email" label="メールアドレス">
          <Input maxLength={255} />
        </Form.Item>
        <Flex gap={12}>
          <Form.Item name="lastName" label="姓" style={{ flex: 1 }}>
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item name="firstName" label="名" style={{ flex: 1 }}>
            <Input maxLength={100} />
          </Form.Item>
        </Flex>
        <AttrFields />
        <Form.Item name="enabled" label="状態" valuePropName="checked">
          <Switch checkedChildren="有効" unCheckedChildren="無効" />
        </Form.Item>
        <Form.Item
          name="roles"
          label="権限(スイートのロール)"
          extra="このユーザーに直接付いている realm ロール。ここに無いロールは触らない。"
        >
          <Select mode="multiple" allowClear options={roleOpts} />
        </Form.Item>
        {user.access.inheritedRoles.length > 0 && (
          <Form.Item
            label="グループ由来の権限"
            extra="グループ・複合ロールから継承しているぶん。ここでは外せない。"
          >
            <Space size={[4, 4]} wrap>
              {user.access.inheritedRoles.map((r) => (
                <Tag key={r}>{r}</Tag>
              ))}
            </Space>
          </Form.Item>
        )}
      </Form>
    </Drawer>
  );
}
